import React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers";
import { useForm, FormProvider } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import RegisterForm from "./RegisterForm";
import { login, signup, updateProfile, ISignup } from "../../../actions/identity";
import identitySelectors from "../../../selectors/identity";
import queryString from "query-string";
import { AuthOrigin, useOAuth } from "../../../hooks/useOAuth";
import { RegisterFailureMessage } from "./SignupFailureMessage";
import { Redirect, useParams } from "react-router-dom";
import AppSettings from "../../../config.js";

export const ConnectedRegisterForm = ({ site }: { site: string }) => {
  const query = queryString.parse(window.location.search) as {
    redirect?: string;
    authErr?: string;
  };
  const params = useParams<{ site: string; email?: string }>();

  const emailFromSearch = new URLSearchParams(window.location.search).get("email");

  const signupForm = useForm({
    mode: "onBlur",
    resolver: yupResolver(
      yup.object().shape({
        firstName: yup.string().required("First name is required"),
        lastName: yup.string().required("Last name is required"),
        email: yup.string().email().required("A valid email is required"),
        password: yup.string().required("You must provide a password").min(8, "Password must be at least 8 characters"),
      }),
    ),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: emailFromSearch || "",
      password: "",
    },
  });
  const email = signupForm.watch("email");
  const dispatch = useDispatch();
  const submit = async (data: any) => {
    const result = await dispatch(signup(data as ISignup));
    if (result.error) return;
    await dispatch(login({ email: data.email, password: data.password }));
    await dispatch(updateProfile({ firstName: data.firstName, lastName: data.lastName }));
  };

  const { initiateOAuth, error } = useOAuth({ isEmployee: false, authOrigin: AuthOrigin.Register });

  const postSignup = useSelector(identitySelectors.postSignup);
  const signupFailure = postSignup.error || error || query.authErr;
  const isSignupLoading = postSignup.loading ?? false;

  const postLogin = useSelector(identitySelectors.postLogin);
  const requireVerification = postLogin.error?.status === 403;
  if (requireVerification) {
    const target =
      !query.redirect?.includes("public") && !query.redirect?.includes(AppSettings.GOGOV_WEB_URL)
        ? null
        : query.redirect || null;
    return (
      <Redirect
        to={{
          pathname: `/${params.site}/public/verify`,
          search: queryString.stringify({
            email,
            ...(target ? { redirect: target } : {}),
          }),
        }}
      />
    );
  }

  const isPublicLogin = window.location.pathname.startsWith(`/${site}/public`);
  const forgotPath = isPublicLogin ? "forgot/public" : "forgot";

  function buildForgotPasswordPath() {
    if (query.redirect) return `/${site}/${forgotPath}?redirect=${encodeURIComponent(query.redirect)}`;
    return `/${site}/${forgotPath}`;
  }

  return (
    <FormProvider {...signupForm}>
      {signupFailure && (
        <RegisterFailureMessage email={signupForm.getValues().email} forgotPasswordPath={buildForgotPasswordPath()} />
      )}
      <RegisterForm
        onSubmit={submit}
        initiateOAuth={initiateOAuth}
        site={site}
        isLoading={isSignupLoading}
        formErrors={signupForm.errors}
      />
    </FormProvider>
  );
};
